// Copyright 2022, Imprivata, Inc.  All rights reserved.

/**
 * Calls the W3C Digital Credentials API to request an identity credential from the user's wallet.
 *
 * @param deviceRequest - Base64url-encoded CBOR device request from pa-digital-credentials-svc (already ISO 18013-5 compliant)
 * @param encryptionInfo - Base64url-encoded encryption information for HPKE
 * @returns Promise resolving to deviceResponse string (base64url-encoded CBOR)
 * @throws Error if DC API is not supported or user cancels
 */
export async function callDigitalCredentialsApi(
  deviceRequest: string,
  encryptionInfo: string,
): Promise<string> {
  // Check if DC API is available
  if (!navigator.credentials) {
    throw new Error('NotSupportedError');
  }

  try {
    const credential = await navigator.credentials.get({
      digital: {
        requests: [
          {
            protocol: 'org-iso-mdoc',
            data: {
              deviceRequest: deviceRequest,
              encryptionInfo: encryptionInfo,
            },
          },
        ],
      },
      mediation: 'required',
    } as CredentialRequestOptions);

    if (!credential) {
      const error = new Error('User aborted credential selection');
      error.name = 'AbortError';
      throw error;
    }

    // Extract deviceResponse from credential
    const deviceResponse = (credential as unknown as { data: string }).data;
    if (!deviceResponse) {
      const error = new Error('Device response is empty');
      error.name = 'AbortError';
      throw error;
    }
    return deviceResponse;
  } catch (error) {
    // Re-throw with error name preserved for caller to handle
    if (error instanceof Error) {
      const err = new Error(error.message);
      err.name = error.name;
      throw err;
    }
    throw error;
  }
}
